import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, Loader2, Plus, CheckCircle2, AlertCircle } from "lucide-react";

// AI chart recommendations (Item 162): every suggestion is dry-run validated server-side.
export function ChartIdeas({ open, onOpenChange, datasets, defaultDatasetId, onAddWidget }) {
  const [datasetId, setDatasetId] = useState(defaultDatasetId || "");
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState(null);
  const dupNames = new Set(datasets.map((d) => d.name).filter((n, i, a) => a.indexOf(n) !== i));
  const dsLabel = (d) => dupNames.has(d.name) ? `${d.name} · ${d.mode === "live" ? "live" : "file"} · ${d.id.slice(0, 4)}` : d.name;

  React.useEffect(() => { if (defaultDatasetId && !datasetId) setDatasetId(defaultDatasetId); }, [defaultDatasetId, datasetId]);

  const suggest = async () => {
    if (!datasetId) { toast.error("Select a dataset first"); return; }
    setLoading(true); setRecs(null);
    try {
      const { data } = await api.post("/ai/recommend-charts", { dataset_id: datasetId, count: 5 });
      setRecs(data.recommendations || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Recommendation failed"); }
    finally { setLoading(false); }
  };

  const add = (r) => {
    onAddWidget({ type: r.type, title: r.title, dataset_id: datasetId, config: r.config });
    toast.success(`Added "${r.title}"`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Lightbulb className="w-5 h-5 text-[color:var(--brand-mint)]" />AI chart recommendations</DialogTitle>
          <DialogDescription>Suggestions are grounded on the governed semantic model and dry-run validated before you see them.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Select value={datasetId} onValueChange={setDatasetId}>
            <SelectTrigger data-testid="ideas-dataset-select" className="bg-[color:var(--surface)] flex-1"><SelectValue placeholder="Select dataset" /></SelectTrigger>
            <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{dsLabel(d)}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="ideas-suggest-btn" onClick={suggest} disabled={loading} className="bg-[color:var(--brand-mint)] hover:brightness-95 text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lightbulb className="w-4 h-4 mr-1" />}Suggest charts
          </Button>
        </div>
        <div className="space-y-2 max-h-[46vh] overflow-auto">
          {recs?.map((r, i) => (
            <div key={i} data-testid="idea-card" className="glass-card p-3 flex items-start gap-3">
              <Badge variant="outline" className="text-[10px] uppercase shrink-0 mt-0.5">{r.type}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[color:var(--ink)] flex items-center gap-1.5">
                  {r.title}
                  {r.validated
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--success)]" title="Validated against your data" />
                    : <AlertCircle className="w-3.5 h-3.5 text-[color:var(--danger)]" title={r.error || "Failed validation"} />}
                </div>
                {r.reason && <p className="text-xs text-[color:var(--ink-3)] mt-0.5">{r.reason}</p>}
              </div>
              <Button data-testid="idea-add-btn" size="sm" variant="outline" className="bg-[color:var(--surface)] shrink-0" disabled={!r.validated} onClick={() => add(r)}>
                <Plus className="w-3.5 h-3.5 mr-1" />Add
              </Button>
            </div>
          ))}
          {recs && !recs.length && <p className="text-sm text-[color:var(--ink-3)] text-center py-6">No usable recommendations — try another dataset.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
